import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mantine/core';
import { IconSettings, IconShare, IconBrandX } from '@tabler/icons-react';

import { getRobot, APIRobotInfo, useWidgetScript } from '@mygpt/app';

import { getWidgetCodeTemp } from '@/components/Widget/Embed/Code';
import { useNavigate } from 'react-router-dom';
import { useGPTStore } from '@/store';
import { isUserLoggedIn } from '@/utils/tool';

export const PreviewPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUrl = new URL(window.location.href);
  const id = currentUrl.searchParams.get('id');
  const { userInfo, fetchUserInfo } = useGPTStore();
  const [robotInfo, setRobotInfo] = useState<APIRobotInfo | null>(null);
  const isAuthenticated = isUserLoggedIn();

  const scriptContent = useMemo(() => (id ? getWidgetCodeTemp(id) : ''), [id]);

  const handleManageBot = () => {
    if (id) {
      navigate(`/bot/${id}/widget-embed`);
    }
  };

  const handleShareToX = () => {
    if (!id) return;

    const shareUrl = `${import.meta.env.VITE_API_BASE_URL}/share/${id}?redirect_url=${
      import.meta.env.VITE_BASE_HOST
    }/widget-page/${id}`;
    const text = `${t('widget.share_text')} \n👉`;

    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(
      shareUrl
    )}`;
    window.open(twitterUrl, '_blank', 'noopener,noreferrer');
  };

  // 获取用户信息（如果已认证但store中没有用户信息）
  useEffect(() => {
    if (!id) return;
    if (isAuthenticated && !userInfo) {
      fetchUserInfo().then(async () => {
        const res = await getRobot({ aiId: id });
        setRobotInfo(res.data);
      });
    }
  }, [isAuthenticated, id]);

  const isOwner = useMemo(() => {
    return userInfo && robotInfo && robotInfo?.user.user_id === userInfo?.user_id;
  }, [robotInfo, userInfo]);

  // 检查数字人配置是否为custom
  const isDigitalHumanCustom = useMemo(() => {
    if (!robotInfo?.configs?.widget_configs) return false;
    try {
      const widgetConfigs = JSON.parse(robotInfo.configs.widget_configs);
      return widgetConfigs?.digitalHuman?.image === 'custom';
    } catch (error) {
      console.warn('Failed to parse widget_configs:', error);
      return false;
    }
  }, [robotInfo]);

  useWidgetScript(scriptContent, !!id);

  const lightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let rafId: number;
    let lastUpdateTime = 0;
    const updateInterval = 1000 / 60; // 约60fps

    const handleMouseMove = (event: MouseEvent) => {
      const currentTime = Date.now();
      if (currentTime - lastUpdateTime < updateInterval) return;

      rafId = requestAnimationFrame(() => {
        if (lightRef.current) {
          const rect = lightRef.current.getBoundingClientRect();
          const x = event.clientX - rect.width / 2;
          const y = event.clientY - rect.height / 2;
          lightRef.current.style.transform = `translate(${x}px, ${y}px)`;
        }
      });

      lastUpdateTime = currentTime;
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <div className="relative flex flex-col items-center justify-center h-screen text-gray-800 bg-gradient-to-br from-violet-300 via-violet-400 to-purple-400 overflow-hidden">
      <div
        ref={lightRef}
        className="pointer-events-none absolute bg-white opacity-40 rounded-full blur-3xl"
        style={{
          width: '200px',
          height: '200px',
          willChange: 'transform',
          transform: 'translate(-50%, -50%)',
          left: 0,
          top: 0,
        }}
      />
      <p className="text-xl text-center max-w-[80%] leading-relaxed mb-8 text-gray-800 relative z-10">
        {t('widget.preview_page_title')}
      </p>
      <div className="flex gap-4 relative z-10">
        {id && isOwner && isDigitalHumanCustom && (
          <Button
            leftSection={<IconBrandX size={16} />}
            onClick={handleShareToX}
            variant="outline"
            size="md"
            className="bg-white/20 backdrop-blur-sm border-white/30 text-gray-800 hover:bg-white/30"
          >
            {t('share')}
          </Button>
        )}
        {id && isOwner && (
          <Button
            leftSection={<IconSettings size={16} />}
            onClick={handleManageBot}
            variant="outline"
            size="md"
            className="bg-white/20 backdrop-blur-sm border-white/30 text-gray-800 hover:bg-white/30"
          >
            {t('widget.manage_bot_button')}
          </Button>
        )}
      </div>
    </div>
  );
};
